// Phase 4 — Composer with file uploads.
// Extracted from Workspace.jsx for modularity. Handles:
//   - Drag-drop anywhere on the composer
//   - Clipboard paste (Cmd/Ctrl+V) of images & files
//   - Paperclip → hidden file picker
//   - Attachment chips above the textarea (image thumbnail or file icon)
//   - Per-attachment removal
//   - Upload progress / failure toasts
//
// The parent (Workspace.jsx) owns the textarea value (`input`) and the
// `attachments` array. We just notify it via callbacks.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Paperclip, Send, Square, Image as ImageIcon, FileText, FileType2, Loader2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  uploadFile, ACCEPT_ATTRIBUTE, ACCEPTED_MIMES, MAX_UPLOAD_BYTES,
} from "@/lib/uploads";

function fileIconFor(att) {
  if (att.kind === "image") return ImageIcon;
  if ((att.mime || "").includes("pdf")) return FileType2;
  return FileText;
}

function bytesLabel(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function AttachmentChip({ att, onRemove }) {
  const Icon = fileIconFor(att);
  const isImage = att.kind === "image" && att.image_b64;
  const isUploading = att._uploading;
  const failed = att._error;

  return (
    <div
      data-testid={`attachment-chip-${att.id || att._tempId}`}
      className={[
        "group/chip relative flex items-center gap-2 rounded-lg border px-2 py-1.5 text-[12px]",
        failed
          ? "border-rose-500/40 bg-rose-500/5 text-rose-300"
          : "border-border bg-background text-foreground",
      ].join(" ")}
    >
      {isImage ? (
        <img
          src={`data:${att.mime};base64,${att.image_b64}`}
          alt={att.name}
          className="h-8 w-8 shrink-0 rounded object-cover"
        />
      ) : (
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded bg-secondary text-muted-foreground">
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
        </span>
      )}
      <div className="min-w-0 max-w-[160px]">
        <p className="truncate text-[12px] font-medium">{att.name}</p>
        <p className="truncate text-[10.5px] text-muted-foreground">
          {failed
            ? failed
            : isUploading
            ? "Uploading…"
            : att.size
            ? `${bytesLabel(att.size)} · ${att.kind === "image" ? "image" : "text"}`
            : att.kind === "image" ? "image" : "text"}
        </p>
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${att.name}`}
          className="ml-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground group-hover/chip:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function Composer({
  input, setInput,
  attachments, setAttachments,
  onSend, onStop,
  loading,
  placeholder,
}) {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const dragCounter = useRef(0);

  // Auto-resize textarea to fit content (one-line default → grows to max-h-[200px]).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  // --- File handling -------------------------------------------------------
  const acceptFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;

    // Quick client-side validation
    for (const f of files) {
      if (f.size > MAX_UPLOAD_BYTES) {
        toast.error(`"${f.name}" is too large (max 10 MB)`);
        continue;
      }
      const isImage = f.type.startsWith("image/");
      const accepted = ACCEPTED_MIMES.includes(f.type) || isImage ||
        /\.(pdf|docx|txt|md|csv|json)$/i.test(f.name);
      if (!accepted) {
        toast.error(`"${f.name}" — unsupported file type`);
        continue;
      }

      // Add a placeholder chip while uploading so the user sees feedback
      const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      setAttachments((prev) => [
        ...prev,
        {
          _tempId: tempId, _uploading: true, kind: isImage ? "image" : "text",
          name: f.name, mime: f.type, size: f.size,
        },
      ]);

      try {
        const att = await uploadFile(f);
        setAttachments((prev) => prev.map((a) => (a._tempId === tempId ? att : a)));
      } catch (e) {
        setAttachments((prev) =>
          prev.map((a) => (a._tempId === tempId ? { ...a, _uploading: false, _error: e.message } : a))
        );
        toast.error(e.message || "Upload failed");
      }
    }
  }, [setAttachments]);

  const removeAttachment = useCallback((idOrTempId) => {
    setAttachments((prev) => prev.filter((a) => (a.id || a._tempId) !== idOrTempId));
  }, [setAttachments]);

  // --- Drag-drop -----------------------------------------------------------
  const onDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragCounter.current += 1;
    setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) { dragCounter.current = 0; setDragActive(false); }
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragActive(false);
    acceptFiles(e.dataTransfer?.files);
  };

  // --- Clipboard paste -----------------------------------------------------
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    const pasted = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) pasted.push(f);
      }
    }
    if (pasted.length > 0) {
      e.preventDefault();
      acceptFiles(pasted);
    }
  };

  // --- Keyboard ------------------------------------------------------------
  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSend(); }
    else if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
  };

  // Disable Send if nothing to send AND no successful attachments
  const successfulAttachments = attachments.filter((a) => !a._uploading && !a._error);
  const canSend = (input.trim().length > 0 || successfulAttachments.length > 0) && !loading;
  const anyUploading = attachments.some((a) => a._uploading);

  return (
    <div className="bg-background px-4 pb-4 pt-2 sm:px-10 sm:pb-6">
      <div className="mx-auto max-w-3xl">
        <div
          data-testid="composer-shell"
          className={[
            "relative rounded-2xl border bg-card shadow-sm transition-colors",
            dragActive
              ? "border-primary/60 ring-2 ring-primary/20"
              : "border-border focus-within:border-primary/40 focus-within:shadow-md",
          ].join(" ")}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
        >
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 border-b border-border/60 px-3 pb-2 pt-2.5">
              {attachments.map((a) => (
                <AttachmentChip
                  key={a.id || a._tempId}
                  att={a}
                  onRemove={() => removeAttachment(a.id || a._tempId)}
                />
              ))}
            </div>
          )}

          <div className="flex items-end gap-1 px-2 py-1.5">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTRIBUTE}
              data-testid="composer-file-input"
              onChange={(e) => {
                acceptFiles(e.target.files);
                e.target.value = ""; // reset so same file can be re-selected
              }}
              className="sr-only"
            />

            {/* Attach button (paperclip) */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid="composer-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="h-9 w-9 shrink-0 rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground"
              aria-label="Attach files"
              title="Attach files (or drag, or paste)"
            >
              <Paperclip className="h-4 w-4" />
            </Button>

            <Textarea
              ref={textareaRef}
              data-testid="composer-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              placeholder={placeholder || "Message…"}
              rows={1}
              className="flex-1 resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-[14px] leading-snug placeholder:text-muted-foreground/70 focus-visible:ring-0 max-h-[200px]"
              style={{ minHeight: "36px" }}
            />

            {/* Send / Stop */}
            {loading ? (
              <Button
                data-testid="composer-stop-btn"
                onClick={onStop}
                size="icon"
                className="h-9 w-9 shrink-0 rounded-lg bg-foreground text-background hover:bg-foreground/90"
                aria-label="Stop generating"
              >
                <Square className="h-3.5 w-3.5 fill-current" />
              </Button>
            ) : (
              <Button
                data-testid="composer-send-btn"
                onClick={onSend}
                disabled={!canSend || anyUploading}
                size="icon"
                className="h-9 w-9 shrink-0 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Drag overlay */}
          {dragActive && (
            <div
              data-testid="composer-drag-overlay"
              className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl bg-primary/10 backdrop-blur-sm"
            >
              <p className="text-[13px] font-medium text-primary">
                Drop files to attach — images, PDF, DOCX, TXT (max 10 MB)
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
